/**
 * API Client - Axios instance with pre-signed session support
 *
 * This client automatically:
 * - Adds bearer token from SessionManager for authenticated API calls
 * - Adds session ID header for request tracking
 * - Handles 401 errors by attempting token refresh
 * - Falls back to CSRF-based authentication when no session exists
 */

import axios from "axios";
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { sessionManager } from "../session/SessionManager";

// Track if we're currently refreshing the token
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that token has been refreshed
 */
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

/**
 * Handle expired session - clear session and redirect to login
 */
function handleSessionExpired(): void {
  sessionManager.clear();
  // Redirect to login page
  window.location.href = "/login";
}

/**
 * Create and configure the API client
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor - add auth headers
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add CSRF token for all requests
      const csrfToken = document.querySelector("meta[name=csrf-token]")?.getAttribute("content");
      if (csrfToken) {
        config.headers["X-CSRF-Token"] = csrfToken;
      }

      // Add bearer token if session exists
      const token = sessionManager.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add session ID if available
      const sessionId = sessionManager.getSessionId();
      if (sessionId) {
        config.headers["X-Session-Id"] = sessionId;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { hasRetried?: boolean };

      // Only handle 401 errors for requests that have a session
      if (error.response?.status === 401 && !originalRequest.hasRetried && sessionManager.getSession()) {
        if (isRefreshing) {
          // Wait for the token refresh to complete
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            });
          });
        }

        originalRequest.hasRetried = true;
        isRefreshing = true;

        try {
          const success = await sessionManager.refreshToken();

          if (success) {
            const newToken = sessionManager.getToken();
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return client(originalRequest);
            }
          }

          // Refresh failed - redirect to login
          handleSessionExpired();
          return Promise.reject(error);
        } catch (refreshError) {
          handleSessionExpired();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}

// Export singleton API client instance
export const apiClient = createApiClient();

// Add 401 response interceptor to the global axios instance.
// Note: Request interceptors (CSRF, bearer token, session ID) are already
// configured in entrypoints/application.js — only the response interceptor is needed here.
export function configureAxiosDefaults(): void {
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { hasRetried?: boolean };

      if (error.response?.status === 401 && !originalRequest.hasRetried && sessionManager.getSession()) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }

        originalRequest.hasRetried = true;
        isRefreshing = true;

        try {
          const success = await sessionManager.refreshToken();

          if (success) {
            const newToken = sessionManager.getToken();
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          }

          handleSessionExpired();
          return Promise.reject(error);
        } catch (refreshError) {
          handleSessionExpired();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

export default apiClient;
